import React, { useMemo, useEffect, useRef, useState } from 'react';
import type { Post, User } from '../types';
import PostCard from './PostCard';
import ArrowLeftIcon from './icons/ArrowLeftIcon';

const POST_VIEWER_TRANSITION_MS = 240;

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      'button, a, input, textarea, select, label, video, [role="button"], [role="slider"], .postcard-media-shell, .postcard-media'
    )
  );
};

interface PostViewerPageProps {
  posts: Post[];
  startIndex: number;
  currentUser: User | null;
  allUsers: User[];
  sourceNavId?: string;
  onClose: () => void;
  onPromptLogin: () => void;
  onViewProfile: (user: User) => void;
  onToggleLike: (postId: string) => void;
  onReactToPost?: (postId: string, emoji: string) => void;
  onAddComment: (postId: string, text: string, parentId?: string | null) => void | Promise<void>;
  onOpenComments: (post: Post) => void;
  onSharePost: (postId: string, recipientId: string) => void;
  onOpenSharePage: (post: Post) => void;
  onToggleSave: (postId: string) => void;
  onDeletePost: (postId: string) => void;
  onBanUser?: (userId: string) => void;
  onReportContent?: (type: 'post', targetId: string) => void;
  onOpenLocation?: (post: Post) => void;
}

const PostViewerPage: React.FC<PostViewerPageProps> = ({
  posts,
  startIndex,
  currentUser,
  allUsers,
  sourceNavId,
  onClose,
  onPromptLogin,
  onViewProfile,
  onToggleLike,
  onReactToPost,
  onAddComment,
  onOpenComments,
  onSharePost,
  onOpenSharePage,
  onToggleSave,
  onDeletePost,
  onBanUser,
  onReportContent,
  onOpenLocation,
}) => {
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchGestureEnabled = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dragX, setDragX] = useState(0);
  const orderedPosts = useMemo(() => {
    const start = Math.max(0, startIndex);
    return [...posts.slice(start), ...posts.slice(0, start)];
  }, [posts, startIndex]);

  // Discover opens the whole grid: render lazily as you scroll, each post shown exactly once (no wrap-around).
  const endless = sourceNavId === 'discover' && orderedPosts.length > 0;
  const [renderCount, setRenderCount] = useState(6);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const visiblePosts = useMemo(() => {
    if (!endless) return orderedPosts;
    return orderedPosts.slice(0, renderCount);
  }, [endless, orderedPosts, renderCount]);
  useEffect(() => {
    if (!endless || renderCount >= orderedPosts.length || !sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setRenderCount((count) => count + 5);
    }, { rootMargin: '1500px 0px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [endless, renderCount, orderedPosts.length]);

  const owner = orderedPosts[0]?.user;
  const allowSwipeClose = sourceNavId !== 'discover';
  const headerLabel = sourceNavId === 'discover'
    ? 'Viewing discover page'
    : owner
      ? `@${owner.username}`.toUpperCase()
      : 'Viewing posts';
  // A rider's posts show just their @tag, in bold.
  const headerIsTag = sourceNavId !== 'discover' && Boolean(owner);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frameId = window.requestAnimationFrame(() => setIsVisible(true));
    return () => {
      window.cancelAnimationFrame(frameId);
      document.body.style.overflow = original;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    if (closeTimerRef.current !== null) return;
    setIsVisible(false);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      onClose();
    }, POST_VIEWER_TRANSITION_MS);
  };

  return (
    <div
      className={`fixed inset-0 z-[1200] bg-app-backdrop text-white overflow-y-auto transition-opacity duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      // No transform at rest: a transformed/scaled ancestor makes WKWebView
      // composite <video> on a separate layer, which renders black or tears
      // while the open animation runs. Only apply one while swipe-dragging.
      style={{
        transform: dragX > 0 ? `translate3d(${dragX}px, 0, 0)` : 'none',
      }}
      onTouchStart={(e) => {
        if (!allowSwipeClose) return;
        touchGestureEnabled.current = !isInteractiveTarget(e.target);
        if (!touchGestureEnabled.current) {
          touchStart.current = null;
          return;
        }
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
        setDragX(0);
      }}
      onTouchMove={(e) => {
        if (!allowSwipeClose) return;
        if (!touchGestureEnabled.current || !touchStart.current) return;
        const t = e.touches[0];
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        if (Math.abs(dx) > Math.abs(dy)) {
          e.preventDefault();
          setDragX(Math.max(0, Math.min(dx, 220)));
        }
      }}
      onTouchEnd={(e) => {
        if (!allowSwipeClose) return;
        if (!touchGestureEnabled.current || !touchStart.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        touchStart.current = null;
        touchGestureEnabled.current = false;
        if (dx > 70 && Math.abs(dx) > Math.abs(dy) + 10) {
          handleClose();
        } else {
          setDragX(0);
        }
      }}
    >
      {sourceNavId !== 'discover' && (
      <header className="sticky top-0 z-10 flex items-center gap-3 px-4 pb-3 bg-app-overlay backdrop-blur-md border-b border-white/10" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}>
        <button
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close post viewer"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className={headerIsTag ? 'text-lg font-black text-white' : 'text-sm text-gray-300'}>{headerLabel}</div>
      </header>
      )}
      <style>{`
        .post-viewer-fullscreen .postcard-container,
        .post-viewer-fullscreen .postcard-media-shell,
        .post-viewer-fullscreen .postcard-media {
          border-radius: 0 !important;
        }
        .post-viewer-fullscreen .postcard-container {
          max-width: none;
          margin: 0 0 1.5rem 0;
        }
      `}</style>
      {/* The bottom navigation stays on top of the viewer, so leave room for it (Discover also drops the header bar). */}
      <div
        className={`post-viewer-fullscreen mx-auto max-w-none px-0 py-0 space-y-6`}
        style={{
          ...(sourceNavId === 'discover' ? { paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.5rem)' } : {}),
          paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 6rem)',
        }}
      >
        {visiblePosts.map((post, idx) => (
          <PostCard
            key={`${post.id}-${idx}`}
            post={post}
            currentUser={currentUser}
            allUsers={allUsers}
            onToggleLike={onToggleLike}
            onReactToPost={onReactToPost}
            onAddComment={onAddComment}
            onOpenComments={onOpenComments}
            onSharePost={onSharePost}
            onOpenSharePage={onOpenSharePage}
            onPromptLogin={onPromptLogin}
            onViewProfile={onViewProfile}
            onToggleSave={onToggleSave}
            onDeletePost={onDeletePost}
            onBanUser={onBanUser}
            onReportContent={onReportContent}
            onOpenLocation={onOpenLocation}
          />
        ))}
        {endless && renderCount < orderedPosts.length && <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />}
      </div>
    </div>
  );
};

export default PostViewerPage;
